import { call, put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { partAttributesApi, PartAttribute, PartAttributeSearchParams } from '../../services/partAttributesApi';
import {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, setPagination, setStatistics, setBulkOperationLoading, setSuccessMessage,
  FETCH_PART_ATTRIBUTES, FETCH_PART_ATTRIBUTE_BY_ID, CREATE_PART_ATTRIBUTE, UPDATE_PART_ATTRIBUTE, DELETE_PART_ATTRIBUTE,
  SEARCH_PART_ATTRIBUTES, FETCH_PART_ATTRIBUTES_STATS, BULK_PART_ATTRIBUTES_ACTIVATE, BULK_PART_ATTRIBUTES_DEACTIVATE,
  BULK_PART_ATTRIBUTES_DELETE, EXPORT_PART_ATTRIBUTES_CSV,
} from '../reducers/partAttributesSlice';

function* fetchPartAttributesSaga(action: PayloadAction<PartAttributeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const state: RootState = yield select();
    const { pagination, searchParams } = state.partAttributes;
    const params = { page: pagination.page, pageSize: pagination.pageSize, ...searchParams, ...action.payload };
    const resp = yield call(partAttributesApi.getAll, params);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: params.page || 1, pageSize: params.pageSize || 10 }));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to fetch Part Attributes'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchPartAttributeByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const item = yield call(partAttributesApi.getById, action.payload);
    yield put(setSelectedItem(item));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to fetch Part Attribute'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createPartAttributeSaga(action: PayloadAction<Omit<PartAttribute, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const errors = yield call(partAttributesApi.validation.validate, action.payload);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const created = yield call(partAttributesApi.create, action.payload);
    yield put(addItem(created));
    yield put(setSuccessMessage('Part Attribute created successfully'));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to create Part Attribute'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updatePartAttributeSaga(action: PayloadAction<{ id: number; item: Partial<PartAttribute> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const { id, item } = action.payload;
    const errors = yield call(partAttributesApi.validation.validate, item);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const updated = yield call(partAttributesApi.update, id, item);
    yield put(updateItem(updated));
    yield put(setSuccessMessage('Part Attribute updated successfully'));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to update Part Attribute'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deletePartAttributeSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const resp = yield call(partAttributesApi.delete, action.payload);
    if (resp.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('Part Attribute deleted successfully'));
    } else {
      yield put(setError(resp.message || 'Failed to delete Part Attribute'));
    }
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to delete Part Attribute'));
  } finally {
    yield put(setLoading(false));
  }
}

function* searchPartAttributesSaga(action: PayloadAction<PartAttributeSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const resp = yield call(partAttributesApi.search, action.payload);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: action.payload.page || 1, pageSize: action.payload.pageSize || 10 }));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to search Part Attributes'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchStatsSaga(): Generator<any, void, any> {
  try {
    const stats = yield call(partAttributesApi.getStatistics);
    yield put(setStatistics(stats));
  } catch (_) {}
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    const errors = yield call(partAttributesApi.validation.validateBulk, action.payload);
    if (errors.length) { yield put(setError(errors[0])); return; }
    const resp = yield call(partAttributesApi.bulkActivate, action.payload);
    if (resp.success) { yield put(setSuccessMessage(resp.message || 'Activated successfully')); yield put({ type: FETCH_PART_ATTRIBUTES }); }
    else { yield put(setError(resp.message || 'Failed to activate')); }
  } finally { yield put(setBulkOperationLoading(false)); }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    const errors = yield call(partAttributesApi.validation.validateBulk, action.payload);
    if (errors.length) { yield put(setError(errors[0])); return; }
    const resp = yield call(partAttributesApi.bulkDeactivate, action.payload);
    if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deactivated successfully')); yield put({ type: FETCH_PART_ATTRIBUTES }); }
    else { yield put(setError(resp.message || 'Failed to deactivate')); }
  } finally { yield put(setBulkOperationLoading(false)); }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    const errors = yield call(partAttributesApi.validation.validateBulk, action.payload);
    if (errors.length) { yield put(setError(errors[0])); return; }
    const resp = yield call(partAttributesApi.bulkDelete, action.payload);
    if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deleted successfully')); yield put({ type: FETCH_PART_ATTRIBUTES }); }
    else { yield put(setError(resp.message || 'Failed to delete')); }
  } finally { yield put(setBulkOperationLoading(false)); }
}

function* exportCsvSaga(action: PayloadAction<PartAttributeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const state: RootState = yield select();
    const params = { ...state.partAttributes.searchParams, ...action.payload };
    const blob = yield call(partAttributesApi.exportToCSV, params);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `part_attributes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
    yield put(setSuccessMessage('Exported successfully'));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to export Part Attributes'));
  } finally { yield put(setLoading(false)); }
}

export function* partAttributesSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_PART_ATTRIBUTES, fetchPartAttributesSaga);
  yield takeEvery(FETCH_PART_ATTRIBUTE_BY_ID, fetchPartAttributeByIdSaga);
  yield takeEvery(CREATE_PART_ATTRIBUTE, createPartAttributeSaga);
  yield takeEvery(UPDATE_PART_ATTRIBUTE, updatePartAttributeSaga);
  yield takeEvery(DELETE_PART_ATTRIBUTE, deletePartAttributeSaga);
  yield takeEvery(SEARCH_PART_ATTRIBUTES, searchPartAttributesSaga);
  yield takeEvery(FETCH_PART_ATTRIBUTES_STATS, fetchStatsSaga);
  yield takeEvery(BULK_PART_ATTRIBUTES_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_PART_ATTRIBUTES_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_PART_ATTRIBUTES_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_PART_ATTRIBUTES_CSV, exportCsvSaga);
} 